import { Save, Send } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  Icon,
  IconButton,
  Snackbar,
  Switch,
  Typography,
} from "@mui/material";
import { ArrayHelpers, Field, FieldArray, Formik, FormikProps } from "formik";
import { TextField } from "formik-mui";
import * as React from "react";
import * as yup from "yup";
import Maybe from "../../../../components/common/Maybe";
import { SaveForm } from "../../../../components/quote/QuoteRequestData";
import { SavedQuotes } from "../../../../utilities/saved-quotes";
import { PurchaseDetails } from "../../../../components/purchase/PurchaseData.interface";

const SaveButton = (props: {
  quoteRequest: PurchaseDetails;
  agentNum: string;
  affiliate?: string;
}): JSX.Element => {
  const [open, setOpen] = React.useState(false);
  const [debug, setDebug] = React.useState(true);

  const [snackbarOptions, setSnackbarOptions] = React.useState<{
    openSuccess: boolean;
    openError: boolean;
  }>({
    openSuccess: false,
    openError: false,
  });

  const toggleDebug = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDebug(event.target.checked);
  };

  const showDebug = false;

  const handleOpenModal = () => {
    setOpen(true);
  };

  const handleCloseModal = () => {
    setOpen(false);
  };

  const handleOpenSnackbar = (success: boolean) => {
    setSnackbarOptions({
      openSuccess: success,
      openError: !success,
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbarOptions({
      openSuccess: false,
      openError: false,
    });
  };

  const submitSaveForm = async (values: SaveForm) => {
    const saveQuote = new SavedQuotes();
    let response = null;

    try {
      response = await saveQuote.saveQuote(
        props.quoteRequest,
        values,
        props.agentNum,
        props.affiliate
      );
    } catch (e: any) {
      console.error(e);
      handleOpenSnackbar(false);
    }

    if (response) {
      handleOpenSnackbar(true);
      handleCloseModal();
    }
  };

  const saveFormInit = {
    primaryEmail: "",
    primaryName: "",
    message: "",
    share: false,
    otherEmails: [{ email: "" }],
  };

  const saveFormValidationSchema = yup.object({
    primaryEmail: yup
      .string()
      .required("Email address is required.")
      .email("Value is not a valid email address."),
    share: yup.boolean(),
    primaryName: yup.string().when("share", {
      is: true,
      then: yup.string().required("Your name is required."),
    }),
    otherEmails: yup.array().when("share", {
      is: true,
      then: yup.array().of(
        yup.object().shape({
          email: yup
            .string()
            .email("Value is not a valid email address.")
            .required("Email address is required."),
        })
      ),
    }),
  });

  return (
    <>
      <IconButton aria-label="save" onClick={handleOpenModal}>
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12.6667 2.5C13.1063 2.50626 13.5256 2.68598 13.8333 3L17 6.16667C17.314 6.47438 17.4937 6.89372 17.5 7.33333V15.8333C17.5 16.2754 17.3244 16.6993 17.0118 17.0118C16.6993 17.3244 16.2754 17.5 15.8333 17.5H4.16667C3.72464 17.5 3.30072 17.3244 2.98816 17.0118C2.67559 16.6993 2.5 16.2754 2.5 15.8333V4.16667C2.5 3.72464 2.67559 3.30072 2.98816 2.98816C3.30072 2.67559 3.72464 2.5 4.16667 2.5H12.6667Z"
            stroke="#212121"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M14.1667 17.5V11.6667C14.1667 11.4456 14.0789 11.2337 13.9226 11.0774C13.7663 10.9211 13.5544 10.8333 13.3333 10.8333H6.66668C6.44566 10.8333 6.2337 10.9211 6.07742 11.0774C5.92114 11.2337 5.83334 11.4456 5.83334 11.6667V17.5"
            stroke="#212121"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M5.83334 2.5V5.83333C5.83334 6.05435 5.92114 6.26631 6.07742 6.42259C6.2337 6.57887 6.44566 6.66667 6.66668 6.66667H12.5"
            stroke="#212121"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </IconButton>

      <Dialog open={open} onClose={handleCloseModal} scroll={"body"}>
        <DialogTitle>Save &amp; Share Your Quote</DialogTitle>

        <Formik
          initialValues={saveFormInit}
          validationSchema={saveFormValidationSchema}
          onSubmit={submitSaveForm}
        >
          {(formikProps: FormikProps<SaveForm>) => {
            return (
              <>
                <DialogContent dividers>
                  <Typography variant="body1">
                    Enter your email to save your quote and view it later.You
                    can also share your quote by clicking the &quot;Share
                    Quote&quot; option below.
                  </Typography>

                  <Box
                    display={"flex"}
                    flexDirection={"column"}
                    justifyContent={"center"}
                    rowGap={2}
                    pt={4}
                  >
                    <Field
                      component={TextField}
                      placeholder={"Your Email Address"}
                      name="primaryEmail"
                      type="text"
                      label="Your Email Address"
                      onChange={formikProps.handleChange}
                      onBlur={formikProps.handleBlur}
                      disabled={formikProps.isSubmitting}
                    />

                    <FormControlLabel
                      label="Share Quote"
                      sx={{ justifyContent: "center" }}
                      control={
                        <Switch
                          name="share"
                          checked={formikProps.values.share}
                          inputProps={{ "aria-label": "controlled" }}
                          onChange={formikProps.handleChange}
                          onBlur={formikProps.handleBlur}
                          disabled={formikProps.isSubmitting}
                        />
                      }
                    />

                    <Maybe test={formikProps.values.share}>
                      <Field
                        component={TextField}
                        placeholder={`From (Your Name)`}
                        name="primaryName"
                        type="text"
                        label={`From (Your Name)`}
                        onChange={formikProps.handleChange}
                        onBlur={formikProps.handleBlur}
                        disabled={formikProps.isSubmitting}
                      />

                      <FieldArray
                        name={`otherEmails`}
                        render={(arrayHelpers: ArrayHelpers) => {
                          return (
                            <>
                              {formikProps.values.otherEmails.map(
                                (email, index) => {
                                  return (
                                    <Grid
                                      container
                                      key={`email-${index}`}
                                      alignItems={"center"}
                                      justifyContent={"center"}
                                    >
                                      <Grid item xs={index == 0 ? 12 : 11}>
                                        <FormControl fullWidth>
                                          <Field
                                            component={TextField}
                                            placeholder={`Email for Recepient #${
                                              index + 1
                                            }`}
                                            name={`otherEmails.${index}.email`}
                                            type="text"
                                            label={`Email for Recepient #${
                                              index + 1
                                            }`}
                                            onChange={formikProps.handleChange}
                                            onBlur={formikProps.handleBlur}
                                            disabled={formikProps.isSubmitting}
                                          />
                                        </FormControl>
                                      </Grid>

                                      <Maybe test={index > 0}>
                                        <Grid
                                          item
                                          xs={1}
                                          justifyContent={"center"}
                                        >
                                          <IconButton
                                            color="error"
                                            aria-label="delete"
                                            onClick={() => {
                                              arrayHelpers.remove(index);
                                            }}
                                          >
                                            <DeleteIcon />
                                          </IconButton>
                                        </Grid>
                                      </Maybe>
                                    </Grid>
                                  );
                                }
                              )}
                              <Button
                                variant="outlined"
                                color="info"
                                size={"small"}
                                type="button"
                                disabled={formikProps.isSubmitting}
                                onClick={() => {
                                  arrayHelpers.push({
                                    email: "",
                                  });
                                }}
                              >
                                <Icon style={{ marginRight: 5 }}>
                                  person_add
                                </Icon>{" "}
                                Add Another Recipient
                              </Button>
                            </>
                          );
                        }}
                      />

                      <Field
                        component={TextField}
                        name={`message`}
                        label={`Add Message (optional)`}
                        onChange={formikProps.handleChange}
                        onBlur={formikProps.handleBlur}
                        disabled={formikProps.isSubmitting}
                        InputProps={{ multiline: true, rows: 3 }}
                      />
                    </Maybe>
                  </Box>

                  {/* <Maybe test={showDebug}>

                    <Grid item container justifyContent="center" xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={debug}
                            onChange={toggleDebug}
                            inputProps={{ 'aria-label': 'controlled' }}
                          />
                        }
                        label="Toggle Debug Info"
                      />
                    </Grid>

                    <Maybe test={debug}>
                      <Grid item container justifyContent="center" xs={12}>
                        <Typography
                          variant={'body1'}
                          gutterBottom
                          align={'center'}
                        >
                          {formikProps.isSubmitting ? 'submitting' : 'not submitting'}
                          <br></br>
                          {formikProps.isValidating ? 'validating' : 'not validating'}
                          <br></br>
                          {formikProps.isValid ? 'valid' : 'not valid'}
                        </Typography>
                      </Grid>

                      <pre style={{ textAlign: "left" }}>
                        <strong>Values</strong>
                        <br />
                        {JSON.stringify(formikProps.values, null, 2)}
                      </pre>
                      <pre style={{ textAlign: "left" }}>
                        <strong>Errors</strong>
                        <br />
                        {JSON.stringify(formikProps.errors, null, 2)}
                      </pre>
                      <pre style={{ textAlign: "left" }}>
                        <strong>Touched</strong>
                        <br />
                        {JSON.stringify(formikProps.touched, null, 2)}
                      </pre>
                    </Maybe>
                  </Maybe> */}
                </DialogContent>

                <DialogActions>
                  <Button
                    onClick={handleCloseModal}
                    disabled={formikProps.isSubmitting}
                  >
                    Close
                  </Button>

                  <LoadingButton
                    onClick={formikProps.submitForm}
                    endIcon={<Send />}
                    loading={formikProps.isSubmitting}
                    loadingPosition="end"
                    variant="contained"
                  >
                    Save/Send
                  </LoadingButton>
                </DialogActions>
              </>
            );
          }}
        </Formik>
        <Snackbar
          open={snackbarOptions.openError}
          onClose={handleCloseSnackbar}
          autoHideDuration={6000}
        >
          <Alert
            severity={"error"}
            onClose={handleCloseSnackbar}
            variant={"filled"}
            sx={{ width: "100%" }}
          >
            Failed to save quote.
          </Alert>
        </Snackbar>
      </Dialog>

      <Snackbar
        open={snackbarOptions.openSuccess}
        onClose={handleCloseSnackbar}
        autoHideDuration={6000}
      >
        <Alert
          severity={"success"}
          onClose={handleCloseSnackbar}
          variant={"filled"}
          sx={{ width: "100%" }}
        >
          Quote saved.
        </Alert>
      </Snackbar>
    </>
  );
};

export default SaveButton;
